import { getContactCompanyIds } from '@/utils/contactCompanies'

export function parseContactsListResponse(data: unknown): any[] {
  return Array.isArray(data) ? data : ((data as { data?: unknown })?.data || []) as any[]
}

export function filterContactsWithNumericId(contacts: unknown[]): Record<string, unknown>[] {
  return contacts.filter((c): c is Record<string, unknown> => typeof (c as { id?: unknown })?.id === 'number')
}

export function normalizeContactRecord(c: unknown): Record<string, unknown> | null {
  const contact = c as { id?: unknown }
  if (contact?.id == null) return null
  const id = Number(contact.id)
  if (!Number.isFinite(id)) return null
  return { ...contact, id }
}

export function mergeContactsBySelectedIds(
  existingContacts: any[],
  fetchedContacts: any[],
  selectedContactIds: number[],
): any[] {
  const selectedIds = new Set(selectedContactIds)
  const mergedById = new Map<number, any>()
  existingContacts.forEach((c: any) => {
    if (c && typeof c.id === 'number' && selectedIds.has(c.id)) {
      mergedById.set(c.id, c)
    }
  })
  fetchedContacts.filter((c: any) => c && typeof c.id === 'number').forEach((c: any) => {
    mergedById.set(c.id, c)
  })
  return Array.from(mergedById.values())
}

export function getCompanyIdsFromSelectedContacts(
  contacts: any[],
  contactIds: number[],
): number[] {
  const selectedContacts = (contactIds || [])
    .map((id) => contacts.find((c: any) => c.id === id))
    .filter(Boolean) as any[]
  return [...new Set(selectedContacts.flatMap((contact) => getContactCompanyIds(contact)))]
}

export function mergeCompaniesById(existingCompanies: any[], fetchedCompanies: any[]): any[] {
  const mergedById = new Map<number, any>()
  existingCompanies.forEach((c: any) => {
    if (c && typeof c.id === 'number') mergedById.set(c.id, c)
  })
  fetchedCompanies.filter((c: any) => c && typeof c.id === 'number').forEach((c: any) => {
    mergedById.set(c.id, c)
  })
  return Array.from(mergedById.values())
}

export function parseCompaniesListResponse(data: any): any[] {
  const raw = data?.data ?? data
  return Array.isArray(raw) ? raw : []
}

export function shouldSyncCompanyIds(previousCompanyIds: number[], nextCompanyIds: number[]): boolean {
  return !(
    nextCompanyIds.length === previousCompanyIds.length &&
    nextCompanyIds.every((id) => previousCompanyIds.includes(id))
  )
}

export function getAddedAndRemovedIds(newIds: number[], oldIds: number[]): { added: number[]; removed: number[] } {
  return {
    added: (newIds || []).filter((id) => !(oldIds || []).includes(id)),
    removed: (oldIds || []).filter((id) => !(newIds || []).includes(id)),
  }
}

export function filterContactIdsAfterCompanyRemoval(
  contactIds: number[],
  contacts: any[],
  removedCompanyIds: number[],
): number[] {
  const removedSet = new Set(removedCompanyIds)
  return (contactIds || []).filter((id: number) => {
    const contact = contacts.find((c: any) => c.id === id)
    if (!contact) {
      return false
    }
    return !getContactCompanyIds(contact).some((companyId) => removedSet.has(companyId))
  })
}

export function mergeContactIds(existingIds: number[], newIds: number[]): number[] {
  return [...new Set([...(existingIds || []), ...newIds])]
}

export function buildNextCompanyIds(previousCompanyIds: number[], companyIdsFromContacts: number[]): number[] {
  return [...new Set([...(previousCompanyIds || []), ...companyIdsFromContacts])]
}

export function shouldSyncContactsOnCompanyChange(syncingCompanyContact: boolean): boolean {
  return !syncingCompanyContact
}
